import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Send } from "lucide-react";
import { api } from "@/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SUBJECTS = ["Product Enquiry", "Custom Fitting", "Order Support", "Dealer / Wholesale", "Other"];

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "Product Enquiry", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const product = params.get("product");
    if (product) {
      setForm((f) => ({
        ...f,
        subject: "Custom Fitting",
        message: `I'm interested in the ${product}. Please share fitting availability and pricing details.`,
      }));
    }
  }, [params]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent", { description: "Our team will get back to you soon." });
      setForm({ name: "", email: "", phone: "", subject: "Product Enquiry", message: "" });
    } catch {
      toast.error("Could not send enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <div className="bg-[#0A0A0A] text-white">
        <div className="container-ping py-14">
          <span className="label-caps text-[#E31837]">Get In Touch</span>
          <h1 className="heading-1 text-white mt-2">Contact Us</h1>
        </div>
      </div>

      <div className="container-ping py-16 grid lg:grid-cols-[1fr_360px] gap-14">
        {/* Form */}
        <form onSubmit={submit} className="space-y-5" data-testid="enquiry-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="name" className="label-caps text-muted-foreground">Name *</Label>
              <Input id="name" value={form.name} onChange={(e) => setField("name", e.target.value)} className="mt-2 rounded-none focus-visible:ring-2 focus-visible:ring-[#0A0A0A]" data-testid="enquiry-name" />
            </div>
            <div>
              <Label htmlFor="email" className="label-caps text-muted-foreground">Email *</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className="mt-2 rounded-none focus-visible:ring-2 focus-visible:ring-[#0A0A0A]" data-testid="enquiry-email" />
            </div>
            <div>
              <Label htmlFor="phone" className="label-caps text-muted-foreground">Phone</Label>
              <Input id="phone" type="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} className="mt-2 rounded-none focus-visible:ring-2 focus-visible:ring-[#0A0A0A]" data-testid="enquiry-phone" />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Subject</Label>
              <Select value={form.subject} onValueChange={(v) => setField("subject", v)}>
                <SelectTrigger className="mt-2 rounded-none" data-testid="enquiry-subject"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="message" className="label-caps text-muted-foreground">Message *</Label>
            <Textarea id="message" rows={6} value={form.message} onChange={(e) => setField("message", e.target.value)} className="mt-2 rounded-none focus-visible:ring-2 focus-visible:ring-[#0A0A0A]" data-testid="enquiry-message" />
          </div>
          <button
            type="submit"
            disabled={sending}
            className="bg-[#E31837] text-white px-10 py-4 label-caps flex items-center gap-2 btn-transition hover:bg-[#b3122c] active:scale-95 disabled:opacity-60"
            data-testid="enquiry-submit"
          >
            {sending ? "Sending..." : <>Send Enquiry <Send className="h-4 w-4" /></>}
          </button>
        </form>

        {/* Info */}
        <aside className="space-y-8">
          <div>
            <h3 className="heading-3 mb-4">Head Office</h3>
            <ul className="space-y-4 text-sm text-muted-foreground">
              <li className="flex items-start gap-3"><MapPin className="h-5 w-5 text-[#E31837] shrink-0" /> Khasra No. 1151/3, Ward No. 7, 4th Floor, Plot No. 1064B, Mehrauli, New Delhi 110030, India</li>
              <li className="flex items-center gap-3"><Phone className="h-5 w-5 text-[#E31837]" /> +91-9899039199</li>
              <li className="flex items-center gap-3"><Mail className="h-5 w-5 text-[#E31837]" /> contact@pinggolf.in</li>
            </ul>
          </div>
          <div className="border border-border p-6 bg-[#F4F4F5]">
            <h4 className="font-head font-bold uppercase tracking-tight text-lg">Store Hours</h4>
            <p className="text-sm text-muted-foreground mt-2">Mon – Sat: 10:00 – 20:00<br />Sunday: 11:00 – 18:00</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
